import React, { useMemo } from 'react';
import MetricChart from './MetricChart';
import { CampaignSuccessMetric } from '../../services/api';

interface MetricsGridProps {
  metrics: CampaignSuccessMetric[];
  filters?: {
    priority?: string[];
    category?: string[];
  };
}

const MetricsGrid: React.FC<MetricsGridProps> = ({ metrics, filters }) => {
  // Sort and filter metrics
  const sortedMetrics = useMemo(() => {
    let filteredMetrics = [...metrics];

    // Apply filters if provided
    if (filters?.priority && filters.priority.length > 0) {
      filteredMetrics = filteredMetrics.filter(m => 
        filters.priority!.includes(m.priority)
      );
    }

    if (filters?.category && filters.category.length > 0) {
      filteredMetrics = filteredMetrics.filter(m => 
        filters.category!.includes(m.category)
      );
    }

    // Sort by priority (high -> medium -> low)
    const priorityOrder: Record<string, number> = {
      'high': 1,
      'medium': 2,
      'low': 3,
    };

    return filteredMetrics.sort((a, b) => {
      const aPriority = priorityOrder[a.priority.toLowerCase()] || 999;
      const bPriority = priorityOrder[b.priority.toLowerCase()] || 999;
      return aPriority - bPriority;
    });
  }, [metrics, filters]);

  // Group metrics by priority
  const groupedMetrics = useMemo(() => {
    const groups: Record<string, CampaignSuccessMetric[]> = {
      high: [],
      medium: [],
      low: [],
    };

    sortedMetrics.forEach(metric => {
      const priority = metric.priority.toLowerCase();
      if (priority in groups) {
        groups[priority].push(metric);
      }
    });

    return groups;
  }, [sortedMetrics]);

  // Helper to render priority section
  const renderPrioritySection = (priority: string, metrics: CampaignSuccessMetric[]) => {
    if (metrics.length === 0) return null;

    const getPriorityStyles = (priority: string) => {
      switch (priority) {
        case 'high':
          return {
            borderColor: 'border-red-200',
            bgColor: 'bg-red-50',
            textColor: 'text-red-900',
            badgeColor: 'bg-red-100 text-red-800',
          };
        case 'medium':
          return {
            borderColor: 'border-yellow-200',
            bgColor: 'bg-yellow-50',
            textColor: 'text-yellow-900',
            badgeColor: 'bg-yellow-100 text-yellow-800',
          };
        case 'low':
          return {
            borderColor: 'border-green-200',
            bgColor: 'bg-green-50',
            textColor: 'text-green-900',
            badgeColor: 'bg-green-100 text-green-800',
          };
        default:
          return {
            borderColor: 'border-gray-200',
            bgColor: 'bg-gray-50',
            textColor: 'text-gray-900',
            badgeColor: 'bg-gray-100 text-gray-800',
          };
      }
    };

    const styles = getPriorityStyles(priority);

    return (
      <div key={priority} className="mb-8">
        <div className="flex items-center mb-4">
          <h3 className={`text-lg font-semibold ${styles.textColor} capitalize`}>
            {priority} Priority Metrics
          </h3>
          <span className={`ml-3 px-2 py-1 text-xs rounded-full ${styles.badgeColor}`}>
            {metrics.length} metric{metrics.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {metrics.map(metric => (
            <MetricChart key={metric.id} metric={metric} />
          ))}
        </div>
      </div>
    );
  };

  // Calculate summary statistics
  const totalMetrics = sortedMetrics.length;
  const metricsByCategory = useMemo(() => {
    const categories: Record<string, number> = {};
    sortedMetrics.forEach(metric => {
      categories[metric.category] = (categories[metric.category] || 0) + 1;
    });
    return categories;
  }, [sortedMetrics]);

  if (sortedMetrics.length === 0) {
    return (
      <div className="bg-[var(--card)] rounded-lg p-8 shadow-sm border border-[var(--border)]">
        <div className="text-center">
          <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">
            No Metrics Available
          </h3>
          <p className="text-[var(--foreground-muted)]">
            No metrics match the current filters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Metrics by Priority */}
      {renderPrioritySection('high', groupedMetrics.high)}
      {renderPrioritySection('medium', groupedMetrics.medium)}
      {renderPrioritySection('low', groupedMetrics.low)}
    </div>
  );
};

export default MetricsGrid;
